/**
 * Date-range helpers for DS Repricing: the ISO strings the API speaks
 * (`YYYY-MM-DD`, inclusive both ends) on one side, the `Date` pairs the
 * calendar picker speaks on the other, and the quick ranges offered
 * next to the calendar.
 *
 * No React and no `@ui-seeker` here so this runs under `node --test`.
 * Dates are built and read in *local* time on purpose: a limit "from
 * 20 Dec" is the operator's 20 Dec, and `toISOString()` would move it
 * a day for anyone east of Greenwich picking it after 23:00.
 */

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/
const pad = (n) => String(n).padStart(2, '0')

/** `Date` → `YYYY-MM-DD` (local), or `null`. */
export function toIso(d) {
	if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** `YYYY-MM-DD` → local midnight `Date`, or `null` for anything else. */
export function fromIso(s) {
	const m = ISO.exec(String(s || '').trim())
	if (!m) return null
	const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
	// `new Date(2026, 1, 31)` rolls into March without complaint; refuse
	// a string the calendar could never have produced.
	return toIso(d) === m[0] ? d : null
}

/**
 * What the picker should hold for a `{from, to}` pair. A range picker
 * can only show two dates or none, so a one-sided value — legal on a
 * price limit, where a missing end means "open" — is shown as a hint in
 * the empty box instead of being bent into a fake two-day range.
 */
export function pickerState(from, to, fmt) {
	const a = fromIso(from), b = fromIso(to)
	if (a && b) return { value: [a, b], placeholder: null }
	if (a) return { value: null, placeholder: `from ${fmt(a)}` }
	if (b) return { value: null, placeholder: `until ${fmt(b)}` }
	return { value: null, placeholder: null }
}

/**
 * `pickerState` that answers the same object — and so the same `value`
 * array — for as long as `from` and `to` stay the same strings.
 *
 * rsuite's DateRangePicker resets its half-picked range whenever `value`
 * changes identity (`useEffect(…, [valueProp])`). Rebuilding `[Date, Date]`
 * on every render meant any re-render between the first and second click
 * (the pending hint does one on the first click) wiped the first day, and
 * the next click landed on an empty selection that snapped the calendars
 * back to this month and the next (Curro's video, 2026-10-30). One cache
 * per field instance.
 */
export function pickerStateCache() {
	let key, state
	return (from, to, fmt) => {
		const next = `${from ?? ''}|${to ?? ''}`
		if (state === undefined || next !== key) {
			key = next
			state = pickerState(from, to, fmt)
		}
		return state
	}
}

/** Picker output → `{from, to}`; a cleared or half-picked range is both `null`. */
export function rangeFromPicker(value) {
	const a = Array.isArray(value) ? value[0] : null
	const b = Array.isArray(value) ? value[1] : null
	if (!a || !b) return { from: null, to: null }
	const [lo, hi] = a <= b ? [a, b] : [b, a]
	return { from: toIso(lo), to: toIso(hi) }
}

/**
 * Where a range being picked on the calendar stands after one more click:
 * the first click opens it (`date` is the pending first day), the second
 * closes it (`null`, the picker now holds both ends), a third opens another.
 * Mirrors rsuite's own idle/selecting toggle so the hint below can follow it
 * (Curro, 2026-10-28: one click on 4 Nov "highlights nothing persistent").
 */
export function nextPending(pending, date) {
	return pending ? null : date
}

/**
 * What the picker says while a range is half picked — in its header and in
 * the box — instead of the resting placeholder: the first day, and that the
 * next click is the last day (the same square again for a single day) and
 * then OK. Without it the picker looks stuck after the first click.
 */
export function pendingHint(date, fmt) {
	return `${fmt(date)} → pick the last day (same day for one), then OK`
}

const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x }
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
const preset = (label, from, to) => ({ label, value: [from, to], placement: 'left' })

/** Quick ranges for looking back — activity, audit. */
export function pastPresets(today = new Date()) {
	const t = startOfDay(today)
	return [
		preset('Today', t, t),
		preset('Last 7 days', addDays(t, -6), t),
		preset('Last 30 days', addDays(t, -29), t),
		preset('This month', new Date(t.getFullYear(), t.getMonth(), 1), t),
	]
}

/**
 * Quick ranges for a pickup date (CU-869fhtp3a, D2): a pickup is in the
 * future, but close — an operator reviews the days the engine is pricing
 * now, not next year's season. Today and Tomorrow give one day in one
 * click, which the calendar itself cannot (a day there is click, click,
 * OK).
 */
export function pickupPresets(today = new Date()) {
	const t = startOfDay(today)
	return [
		preset('Today', t, t),
		preset('Tomorrow', addDays(t, 1), addDays(t, 1)),
		preset('Next 7 days', t, addDays(t, 6)),
		preset('Next 30 days', t, addDays(t, 29)),
	]
}

/** Quick ranges for planning ahead — limits, schedule windows. */
export function futurePresets(today = new Date()) {
	const t = startOfDay(today)
	const y = t.getFullYear()
	return [
		preset('Next 30 days', t, addDays(t, 29)),
		preset('Rest of this year', t, new Date(y, 11, 31)),
		preset('Next year', new Date(y + 1, 0, 1), new Date(y + 1, 11, 31)),
		preset('Next 12 months', t, addDays(new Date(y + 1, t.getMonth(), t.getDate()), -1)),
	]
}

/**
 * The dates of another row, offered as a one-click preset: building a
 * season of limits means giving every row the same two dates, and that
 * used to mean retyping them (Curro, 2026-09-28). Returns `null` when
 * the other row has no complete range to copy.
 */
export function sameAsPreset(from, to, label = 'Same as row above') {
	const a = fromIso(from), b = fromIso(to)
	return a && b ? preset(label, a, b) : null
}
